import {  t  } from "@/client/libs/dummy-i18n";
import { 
  CreditCardIcon,
  CrownIcon,
  GearIcon, 
  LockKeyIcon, 
  MagicWandIcon, 
  ShieldWarningIcon, 
  UserIcon
} from "@phosphor-icons/react";
import { 
  ScrollArea,
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@stackmorph/ui";
import { cn } from "@stackmorph/utils";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

import { BillingHistory } from "../premium/_sections/billing-history";
import { AccountSettings } from "./_sections/account";
import { DangerZoneSettings } from "./_sections/danger";
import { OpenAISettings } from "./_sections/openai";
import { PremiumSettings } from "./_sections/premium";
import { ProfileSettings } from "./_sections/profile";
import { SecuritySettings } from "./_sections/security";

export const SettingsPage = () => {
  const tabs = [
    { id: "general", label: t`General`, icon: GearIcon, component: <ProfileSettings /> },
    { id: "account", label: t`Account`, icon: UserIcon, component: <AccountSettings /> },
    { id: "billing", label: t`Billing`, icon: CreditCardIcon, component: <BillingHistory /> },
    { id: "security", label: t`Security`, icon: LockKeyIcon, component: <SecuritySettings /> },
    { id: "premium", label: t`Premium`, icon: CrownIcon, component: <PremiumSettings /> },
    { id: "openai", label: t`OpenAI`, icon: MagicWandIcon, component: <OpenAISettings /> },
    { id: "danger", label: t`Account Deletion`, icon: ShieldWarningIcon, component: <DangerZoneSettings /> },
  ];

  return (
    <>
      <Helmet>
        <title>
          {t`Settings`} - {t`StackMorph Resume Builder`}
        </title>
      </Helmet>

      <div className="flex h-[calc(100vh-64px)] flex-col overflow-hidden p-6 lg:p-10">
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8 shrink-0 text-3xl font-bold tracking-tight"
        >
          {t`Settings`}
        </motion.h1>
        
        <Tabs defaultValue="general" orientation="vertical" className="grid h-full gap-10 lg:grid-cols-[250px_1fr]">
          <TabsList className="flex h-auto w-full flex-row overflow-x-auto rounded-xl border border-border/50 bg-background/50 p-2 backdrop-blur-sm self-start lg:flex-col lg:items-stretch lg:space-y-1">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className={cn(
                  "justify-start gap-3 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition-all",
                  "text-foreground/70 hover:text-foreground hover:bg-secondary/50",
                  "data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                )}
              >
                <tab.icon size={18} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-border/50 bg-background/50 backdrop-blur-sm">
            <ScrollArea className="h-full">
              <div className="p-6 lg:p-8">
                {tabs.map((tab) => (
                  <TabsContent
                    key={tab.id}
                    value={tab.id}
                    className="mt-0 outline-none"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="mb-8 flex items-center gap-4">
                        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <tab.icon size={24} />
                        </div>
                        <div>
                          <h2 className="text-2xl font-semibold">{tab.label}</h2>
                          <p className="text-sm text-muted-foreground">
                            {t`Manage your settings`}
                          </p>
                        </div>
                      </div>
                      
                      {tab.component}
                    </motion.div>
                  </TabsContent>
                ))}
              </div>
            </ScrollArea>
          </div>
        </Tabs>
      </div>
    </>
  );
};
